// src/components/organizer/dashboard/OrganizerDashboard.jsx
import React, { useEffect } from "react";
import { getImageUrl } from "@/utils/imageUrl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchOrganizerEvents } from "@/store/slices/organizerSlice";
import { Calendar, CheckCircle, Clock, Users, Plus, ArrowRight, Building2 } from "lucide-react";
import StatCard from "@/components/common/StatCard";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";

const OrganizerDashboard = () => {
  const dispatch = useDispatch();
  const { organizer, events, isLoading } = useSelector((state) => state.organizer);

  useEffect(() => {
    dispatch(fetchOrganizerEvents());
  }, [dispatch]);

  // Calculate stats
  const totalEvents = events.length;
  const publishedEvents = events.filter(e => e.status === "published").length;
  const pendingEvents = events.filter(e => e.status === "submitted" || e.status === "sponsor_listed").length;
  const totalRegistrations = events.reduce((sum, e) => sum + (e.registration_count || 0), 0);

  // Get latest events (up to 5)
  const latestEvents = [...events]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { tone: "info", label: "Submitted" },
      sponsor_listed: { tone: "info", label: "Sponsor Listed" },
      published: { tone: "success", label: "Published" },
      archived: { tone: "danger", label: "Archived" },
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return <Badge tone={config.tone} size="sm">{config.label}</Badge>;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0">
            {organizer?.logo_url ? (
               <img 
                 src={getImageUrl(organizer.logo_url)} 
                 alt={organizer.organization_name || "Organization Logo"} 
                 className="w-full h-full object-cover" 
               />
            ) : (
               <Building2 className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div>
            <motion.h1
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100"
            >
              Welcome back, {organizer?.organization_name || "Organizer"}
            </motion.h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Here's an overview of your events
            </p>
          </div>
        </div>
        <Link to="/organizer/events/create">
          <Button className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatCard
            title="Total Events"
            value={totalEvents}
            icon={<Calendar className="w-6 h-6" />}
            iconBgColor="bg-amber-100 dark:bg-amber-900/30"
            iconColor="text-amber-600 dark:text-amber-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCard
            title="Published"
            value={publishedEvents}
            icon={<CheckCircle className="w-6 h-6" />}
            iconBgColor="bg-green-100 dark:bg-green-900/30"
            iconColor="text-green-600 dark:text-green-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatCard
            title="Pending Approval"
            value={pendingEvents}
            icon={<Clock className="w-6 h-6" />}
            iconBgColor="bg-amber-100 dark:bg-amber-900/30"
            iconColor="text-amber-600 dark:text-amber-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StatCard
            title="Total Registrations"
            value={totalRegistrations}
            icon={<Users className="w-6 h-6" />}
            iconBgColor="bg-orange-100 dark:bg-orange-900/30"
            iconColor="text-orange-600 dark:text-orange-400"
          />
        </motion.div>
      </div>

      {/* Latest Events */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Latest Events
          </h2>
          <Link
            to="/organizer/events"
            className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 flex items-center gap-1"
          >
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Loading events...
          </div>
        ) : latestEvents.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No events yet. Create your first event!
            </p>
            <Link to="/organizer/events/create">
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Create Event
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {latestEvents.map((event) => (
              <Link
                key={event._id}
                to={`/organizer/events/${event._id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-800 dark:text-gray-100 truncate">
                    {event.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(event.start_date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {event.location?.city && ` • ${event.location.city}`}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(event.status)}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {event.registration_count || 0} reg.
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OrganizerDashboard;
